import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useStudioErstellen } from "../../store/studioErstellenStore";
import { useStudioEditor } from "../../store/studioEditorStore";

import classes from "./Rules.module.css";
import priceClasses from "./price/Price.module.css";

import ErrorMessage from "./ErrorMessage";
import { SmallModal } from "../UI/SmallModal/SmallModal";

// Map frontend rule values to backend houseRules field names
const ruleMapping = {
    'smoking': 'smokingAllowed',
    'alcohol': 'alcoholAllowed',
    'food': 'foodAllowed',
    'fire': 'pyrotechnicsAllowed',
    'party': 'glitterConfettiAllowed',
    'adult': 'adultContentAllowed',
    'fogMachine': 'fogMachineAllowed',
    'equipmentChanges': 'equipmentChangesAllowed'
};

// Reverse mapping: backend field names to frontend rule values
const reverseRuleMapping = {
    'smokingAllowed': 'smoking',
    'alcoholAllowed': 'alcohol',
    'foodAllowed': 'food',
    'pyrotechnicsAllowed': 'fire',
    'glitterConfettiAllowed': 'party',
    'adultContentAllowed': 'adult',
    'fogMachineAllowed': 'fogMachine',
    'equipmentChangesAllowed': 'equipmentChanges'
};

export const Rules = ({ validate, fromEditor, studio }) => {
    const [open, setOpen] = useState(false);
    const { studioData, updateStudioData } = useStudioErstellen();
    const { updateField, updatedFields } = useStudioEditor();
    const hasUpdatedHouseRules = Object.prototype.hasOwnProperty.call(updatedFields, 'houseRules');
    const editorHouseRules = hasUpdatedHouseRules ? updatedFields.houseRules : studio?.houseRules;
    const [textareaValue, setTextareaValue] = useState(
        fromEditor ? (editorHouseRules?.customRules || "") : ""
    );
    const [modalError, setModalError] = useState("");
    const [openMobile, setOpenMobile] = useState(false);

    // Convert backend houseRules object to frontend rules array format
    const convertHouseRulesToRulesArray = useCallback((houseRules) => {
        if (!houseRules) return null;
        
        const rules = [];
        Object.keys(reverseRuleMapping).forEach(backendField => {
            const frontendValue = reverseRuleMapping[backendField];
            if (houseRules[backendField] !== undefined && houseRules[backendField] !== null) {
                rules.push({
                    value: frontendValue,
                    allowed: houseRules[backendField]
                });
            } else {
                rules.push({
                    value: frontendValue,
                    allowed: null
                });
            }
        });
        
        return rules;
    }, []);

    // Convert frontend rules array to backend houseRules object format
    const convertRulesArrayToHouseRules = useCallback((rules, customRules) => {
        if (!rules || !Array.isArray(rules)) return null;

        const houseRules = {
            equipmentChangesAllowed: false // Default value
        };

        rules.forEach(rule => {
            if (rule.value && ruleMapping[rule.value] && rule.allowed !== null) {
                houseRules[ruleMapping[rule.value]] = rule.allowed === true;
            }
        });

        // Ensure all required fields are present (set to false if not specified)
        const requiredFields = [
            'smokingAllowed', 'alcoholAllowed', 'foodAllowed', 
            'equipmentChangesAllowed', 'pyrotechnicsAllowed', 
            'glitterConfettiAllowed', 'adultContentAllowed', 'fogMachineAllowed'
        ];
        requiredFields.forEach(field => {
            if (houseRules[field] === undefined) {
                houseRules[field] = false;
            }
        });

        // Add custom rules
        if (customRules && customRules.trim()) {
            houseRules.customRules = customRules;
        } else {
            houseRules.customRules = null;
        }

        return houseRules;
    }, []);

    // Initialize from studio when in editor mode
    useEffect(() => {
        if (fromEditor && editorHouseRules) {
            const convertedRules = convertHouseRulesToRulesArray(editorHouseRules);
            if (convertedRules) {
                setSelectedOptions(convertedRules);
            }
            setTextareaValue(editorHouseRules.customRules || "");
        }
        // Rehydrate from updatedFields on remount; user clicks update local state directly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromEditor, studio, convertHouseRulesToRulesArray]);

    // Initialize textarea with existing rule if it exists (for create flow)
    useEffect(() => {
        if (!fromEditor && studioData.extraRules && studioData.extraRules.length > 0) {
            setTextareaValue(studioData.extraRules[0].title);
        }
    }, [fromEditor, studioData.extraRules]);

    const handleMobileClose = () => {
        // Don't reset textarea value when closing
        setOpenMobile(false);
    };

    const handleClose = () => {
        // Don't reset textarea value when closing
        setOpen(false);        
    };

    const options = [
        { value:"smoking", title: "Rauchen & Dampfen erlaubt"},
        { value:"alcohol", title: "Alkoholkonsum erlaubt" },
        { value:"food", title: "Essen im Studiobereich erlaubt" },
        { value:"fire", title: "Flammen & Pyrotechnik erlaubt" },
        { value:"party", title: "Glitzer, Konfetti oder kleine Partikel erlaubt" },
        { value:"adult", title: "Erwachsenen-Inhalte oder Nacktdrehs erlaubt" },
        { value:"fogMachine", title: "Nebelmaschinen erlaubt" },
        { value:"equipmentChanges", title: "Änderungen an der Studio-Ausstattung erlaubt" }
    ];

    // Then initialize state using options
    const [selectedOptions, setSelectedOptions] = useState(() => {
        if (fromEditor && editorHouseRules) {
            const convertedRules = convertHouseRulesToRulesArray(editorHouseRules);
            if (convertedRules) {
                return convertedRules;
            }
        }
        if (!fromEditor && studioData.rules && studioData.rules.length > 0) {
            return studioData.rules;
        }
        return options.map(opt => ({ value: opt.value, allowed: null }));
    });

    const handleTextareaChange = (e) => {
        setTextareaValue(e.target.value);
        if (e.target.value.length === 400) {
            setModalError("Maximal 400 Zeichen erlaubt.");

        } else {
            setModalError("");
        }
    };

    const handleSave = () => {
        if (textareaValue.trim()) {
            if (fromEditor) {
                // In editor mode, update houseRules with customRules
                const houseRules = convertRulesArrayToHouseRules(selectedOptions, textareaValue);
                if (houseRules) {
                    updateField('houseRules', houseRules);
                }
            } else {
                // In create mode, use extraRules
                const rule = {
                    value: 'custom-rules',
                    title: textareaValue,
                    type: 'extraRule'
                };
                updateStudioData('extraRules', [rule]);
            }
            setOpen(false);
            setOpenMobile(false);
        }
    };

    const [errors, setErrors] = useState({
        rules: ""
    });

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            rules: ""
        };

        if (selectedOptions.some(option => option.allowed === null)) {
            newErrors.rules = "Bitte triff für alle Optionen eine Auswahl.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };
    
    const handleOptionChange = (value, allowed) => {        
        const newSelectedOptions = selectedOptions.map(option =>
            option.value === value ? { ...option, allowed } : option
        );
        setSelectedOptions(newSelectedOptions);
        
        if (fromEditor) {
            // In editor mode, convert to houseRules format and update
            const customRules = textareaValue.trim() || null;
            const houseRules = convertRulesArrayToHouseRules(newSelectedOptions, customRules);
            if (houseRules) {
                updateField('houseRules', houseRules);
            }
        } else {
            // In create mode, use rules array
            updateStudioData('rules', newSelectedOptions);
        }
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, selectedOptions]);

  return (
    <div className={classes.mainContainer}>
        <div className={classes.titleContainer}>
            <h2 className={`${classes.title} titleSemibold2`}>Hausregeln</h2>
            <span style={{display: fromEditor ? 'none' : 'block', color: "var(--dark-grey-4)"}} className={`${classes.text} regularSmall`}>Wähle die passenden Regeln aus, die für dein Studio gelten.</span>
        </div>
        <div className={classes.bottomContainer}>
            {options.map((option) => {
                    const currentSelection = selectedOptions.find(opt => opt.value === option.value);
                    return (
                        <div 
                            key={option.value} 
                            className={classes.option}
                        >
                            <label style={{maxWidth: "295px"}} className="regularMiddle">{option.title}</label>
                            <div className={classes.optionButtons}>
                                {currentSelection?.allowed === true ? (
                                    <button 
                                        type="button"
                                        className={classes.svgButton}
                                        onClick={() => handleOptionChange(option.value, true)}
                                    >
                                    <svg 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        width="34" 
                                        height="34" 
                                        viewBox="0 0 34 34" 
                                        fill="none"

                                    >
                                        <rect width="34" height="34" rx="17" fill="#1F1F1F"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M24 12.9517L14.1704 22.3011L14.1702 22.3009L14.1693 22.3019L10 18.0429L10.9385 17.1241L14.2024 20.4581L23.0948 12L24 12.9517Z" fill="white"/>
                                    </svg>
                                    </button>
                                ) : (
                                    <button 
                                        type="button"
                                        className={classes.svgButton}
                                        onClick={() => handleOptionChange(option.value, true)}
                                    >
                                    <svg 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        width="34" 
                                        height="34" 
                                        viewBox="0 0 34 34" 
                                        fill="none"

                                    >
                                        <rect x="0.5" y="0.5" width="33" height="33" rx="16.5" stroke="#B1B6BB"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M24 12.9517L14.1704 22.3011L14.1702 22.3009L14.1693 22.3019L10 18.0429L10.9385 17.1241L14.2024 20.4581L23.0948 12L24 12.9517Z" fill="#B1B6BB"/>
                                    </svg>
                                    </button>
                                )}
                            {currentSelection?.allowed === false ? (
                                <button 
                                    type="button"
                                    className={classes.svgButton}
                                    onClick={() => handleOptionChange(option.value, false)}
                                >
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="34" 
                                    height="34" 
                                    viewBox="0 0 34 34" 
                                    fill="none"
                                >
                                    <rect width="34" height="34" rx="17" fill="#1F1F1F"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M16.2425 17L10.5 11.2575L11.2575 10.5L17 16.2425L22.7425 10.5L23.5 11.2575L17.7575 17L23.5 22.7425L22.7425 23.5L17 17.7575L11.2575 23.5L10.5 22.7425L16.2425 17Z" fill="white"/>
                                </svg>
                                </button>
                            ) : (
                                <button 
                                    type="button"
                                    className={classes.svgButton}
                                    onClick={() => handleOptionChange(option.value, false)}
                                >
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="34" 
                                    height="34" 
                                    viewBox="0 0 34 34" 
                                    fill="none"

                                >
                                    <path fillRule="evenodd" clipRule="evenodd" d="M16.2425 17L10.5 11.2575L11.2575 10.5L17 16.2425L22.7425 10.5L23.5 11.2575L17.7575 17L23.5 22.7425L22.7425 23.5L17 17.7575L11.2575 23.5L10.5 22.7425L16.2425 17Z" fill="#B1B6BB"/>
                                    <rect x="0.5" y="0.5" width="33" height="33" rx="16.5" stroke="#B1B6BB"/>
                                </svg>
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
            <button 
                className={classes.addRuleButton} 
                onClick={() => {
                    window.innerWidth <= 450 ? setOpenMobile(true) : setOpen(true)
                }}
            >
                {((fromEditor && textareaValue.trim()) || (!fromEditor && studioData.extraRules && studioData.extraRules.length > 0))
                    ? "Eigene Hausregeln bearbeiten" 
                    : "Eigene Hausregeln schreiben"
                }
            </button>
            <div className={classes.userRules}>
                {fromEditor && textareaValue.trim() && (
                    <span className="regularMiddle">
                        {textareaValue.length > 100 
                            ? `${textareaValue.slice(0, 100)}...` 
                            : textareaValue
                        }
                    </span>
                )}
                {!fromEditor && studioData.extraRules && studioData.extraRules.length > 0 && (
                    <span className="regularMiddle">
                        {studioData.extraRules[0].title.length > 100 
                            ? `${studioData.extraRules[0].title.slice(0, 100)}...` 
                            : studioData.extraRules[0].title
                        }
                    </span>
                )}
            </div>
            {errors.rules && <ErrorMessage>{errors.rules}</ErrorMessage>}
            </div>
            {openMobile &&
                typeof document !== "undefined" &&
                createPortal(
                    <div className={priceClasses.mobileSheetOverlay} onClick={handleMobileClose}>
                        <div className={priceClasses.mobileSheet} onClick={(e) => e.stopPropagation()}>
                            <div className={priceClasses.mobileSheetHeader}>
                                <span className="semiboldMiddle">Eigene Hausregeln schreiben</span>
                                <button
                                    type="button"
                                    className={priceClasses.mobileSheetClose}
                                    onClick={handleMobileClose}
                                >
                                    ×
                                </button>
                            </div>
                            <div className={priceClasses.mobileSheetBody}>
                                <div className={classes.modalContent}> 
                                    <textarea 
                                        maxLength="400" 
                                        value={textareaValue}
                                        onChange={handleTextareaChange} 
                                        className={`${classes.modalTextarea} slimRegularLarge`} 
                                        placeholder="Hier Regeln für dein Studio schreiben..."
                                    ></textarea>
                                    <div className={classes.characterCounter}>
                                        <span className={`${classes.characterCount} slimRegularSmallText`}>{textareaValue.length}</span> 
                                        <span className="slimRegularSmallText">/ 400</span>
                                    </div>
                                    {modalError && <ErrorMessage>{modalError}</ErrorMessage>}
                                </div>
                                <button
                                    type="button"
                                    className={`${priceClasses.mobileSheetPrimary} mediumLarge`}
                                    onClick={handleSave}
                                    disabled={textareaValue.length === 0}
                                >
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }
            {open && !openMobile && (
                <div className={classes.modal}>
                    <SmallModal fromBookingDetails={true} open={open} onBackdropClick={handleClose} className={classes.smallModal}>
                        <header className={classes.modalHeader}>
                            <h2 className={`${classes.modalTitle} titleSemibold3`}>Eigene Hausregeln schreiben</h2>
                            <button className={classes.modalCloseButton} onClick={handleClose}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                                </svg>
                            </button>
                        </header>
                        <div className={classes.modalContent}> 
                            <textarea 
                                maxLength="400" 
                                value={textareaValue}
                                onChange={handleTextareaChange} 
                                className={`${classes.modalTextarea} slimRegularLarge`} 
                                placeholder="Hier Regeln für dein Studio schreiben..."
                            ></textarea>
                            <div className={classes.characterCounter}>
                                <span className={`${classes.characterCount} slimRegularSmallText`}>{textareaValue.length}</span> 
                                <span className="slimRegularSmallText">/ 400</span>
                            </div>
                            {modalError && <ErrorMessage>{modalError}</ErrorMessage>}
                        </div>
                        <footer className={classes.modalFooter}>
                            <button 
                                onClick={handleClose}
                                className={`${classes.modalCancelButton} mediumLarge`}
                            >
                                Abbrechen
                            </button>
                            <button 
                                onClick={handleSave}
                                disabled={textareaValue.length === 0}
                                className={`${classes.modalSaveButton} mediumLarge`}
                            >
                                Speichern
                            </button>
                        </footer>
                    </SmallModal>
                </div>
            )}
    </div>
  );
};

export default Rules;
